import {DropResult} from "@hello-pangea/dnd";
import {captureException} from "@sentry/react";
import {useCallback, useState} from "react";
import {usePromiseState} from "./promiseState";

export type OptimisticOrder<T> = {
  /** The items to render: the optimistic order while a move is in flight, otherwise `items`. */
  items: T[];
  /** True while a move is being persisted (and refetched). */
  reordering: boolean;
  /**
   * Show `order` immediately, persist it, then reveal the refetched order.
   *
   * `persist` must not resolve until the refetch reflecting the move has landed in the query cache
   * (e.g. `await invalidateQueries(...)`); the overlay clears when it resolves, so resolving early
   * flaps the list back to the pre-move order until the refetch arrives.
   */
  reorder: (order: T[], persist: () => Promise<unknown>) => void;
};

/**
 * Optimistic overlay + single-flight persistence for a server-ordered list.
 *
 * Runs one move at a time: a server that re-derives ordinals from stored order can commit
 * overlapping requests out of order and persist the wrong result, so a `reorder` while a move is
 * already in flight is ignored. Callers compute the new order (the primitive doesn't assume a
 * layout) and own the drag JSX; see {@link useOptimisticReorder} for the flat single-list convenience.
 */
export function useOptimisticOrder<T>(items: T[]): OptimisticOrder<T> {
  const [overlay, setOverlay] = useState<T[] | null>(null);

  const [state, run] = usePromiseState(async (order: T[], persist: () => Promise<unknown>) => {
    setOverlay(order);
    try {
      await persist();
    } catch (e) {
      captureException(e);
    } finally {
      setOverlay(null);
    }
  }, []);

  const reorder = useCallback(
    (order: T[], persist: () => Promise<unknown>) => {
      // A move already in flight rejects with OperationInProgressError; drop it silently.
      void run(order, persist).catch(() => {});
    },
    [run],
  );

  return {items: overlay ?? items, reordering: state.inProgress, reorder};
}

export type OptimisticReorder<T, Id extends string> = {
  items: T[];
  reordering: boolean;
  /** Move an item to an absolute target index — for programmatic moves (e.g. a move up/down menu). */
  reorderTo: (id: Id, targetIndex: number) => void;
  /** `onDragEnd` handler for a single flat `Droppable`. */
  onDragEnd: (result: DropResult) => void;
};

/**
 * Flat single-`Droppable` optimistic reordering for a server-ordered list, over
 * {@link useOptimisticOrder}. Computes the new order from a drag or an absolute target index.
 */
export function useOptimisticReorder<T, Id extends string = string>(
  items: T[],
  getId: (item: T) => Id,
  persist: (id: Id, targetIndex: number) => Promise<unknown>,
): OptimisticReorder<T, Id> {
  const {items: displayItems, reordering, reorder} = useOptimisticOrder(items);

  const reorderTo = useCallback(
    (id: Id, targetIndex: number) => {
      const from = items.findIndex(item => getId(item) === id);
      if (from === -1 || from === targetIndex) {
        return;
      }
      const reordered = [...items];
      const [moved] = reordered.splice(from, 1);
      reordered.splice(targetIndex, 0, moved);
      reorder(reordered, () => persist(id, targetIndex));
    },
    [items, getId, persist, reorder],
  );

  const onDragEnd = useCallback(
    (result: DropResult) => {
      const {source, destination} = result;
      if (destination == null || destination.index === source.index) {
        return;
      }
      reorderTo(getId(displayItems[source.index]), destination.index);
    },
    [displayItems, getId, reorderTo],
  );

  return {items: displayItems, reordering, reorderTo, onDragEnd};
}
